import * as Model from "../../model"
import * as PpiDomain from "./ppi-domain"
import * as ProspectiveDomain from "./prospective-domain"
import * as ObservationDomain from "./observation-domain"
import * as RestDomain from "./rest-domain"
import Operations from "../../component/scenarii/common/operations"
import {
	_aggregateOrderOperationsTotal,
	reduceTotalPpi
} from "../../component/ppi/ppi-detail/table-rows"
import {
	_aggregateCAFBrute
} from "../../component/scenarii/detail/table-definition"

/**
 *
 * @param localAuthority
 * @param scenario
 * @param ppi
 * @param loanDecompositions
 * @param years
 * @param receipts
 */
export function calculateOperation(
	localAuthority: Model.LocalAuthority,
	scenario: Model.Scenario,
	ppi: Model.Ppi,
	loanDecompositions: Model.LoanDecomposition[],
	years: number[],
	receipts: boolean
): number[] {
	return years.map(year => {
		if (!ppi || !scenario || scenario.year > year)
			return receipts
			? ObservationDomain.calculateObservationOperationReceiptsForYear(
				localAuthority.observations, year)
			: ObservationDomain.calculateObservationOperationExpensesForYear(
				localAuthority.observations, year)

		return ProspectiveDomain.calculateBudget(
			localAuthority, scenario, ppi, loanDecompositions, year, receipts)
	})
}

/**
 * Calcule les dépenses de fonctionnement en s'appuyant sur l'année du scénario
 * pour distinguer le rétrospectif du prospectif.
 * @param localAuthority
 * @param scenario
 * @param ppi
 * @param loanDecompositions
 * @param years
 * @see ObservationDomain.calculateObservationOperationExpensesForYear
 */
export function calculateOperationExpenses(
	localAuthority: Model.LocalAuthority,
	scenario: Model.Scenario,
	ppi: Model.Ppi,
	loanDecompositions: Model.LoanDecomposition[],
	years: number[]
): number[] {
	return calculateOperation(
		localAuthority, scenario, ppi, loanDecompositions, years, false)
}

/**
 * Calcule les recettes de fonctionnement en s'appuyant sur l'année du scénario
 * pour distinguer le rétrospectif du prospectif.
 * @param localAuthority
 * @param scenario
 * @param ppi
 * @param loanDecompositions
 * @param years
 * @see ObservationDomain.calculateObservationOperationReceiptsForYear
 */
export function calculateOperationReceipts(
	localAuthority: Model.LocalAuthority,
	scenario: Model.Scenario,
	ppi: Model.Ppi,
	loanDecompositions: Model.LoanDecomposition[],
	years: number[]
): number[] {
	return calculateOperation(
		localAuthority, scenario, ppi, loanDecompositions, years, true)
}

/**
 * Calcule le résultat, soit les recettes moins les dépenses.
 * @param receipts
 * @param expense
 */
export function calculateResult(receipts: number, expense: number): number {
	return receipts - expense
}

/**
 * Calcule les résultats, soit les recettes moins les dépenses.
 * @param receipts
 * @param expenses
 * @see calculateResult
 */
export function calculateResults(
	receipts: number[],
	expenses: number[]
): number[] {
	return receipts.map((r, rIndex) => calculateResult(r, expenses[rIndex]))
}

/**
 * Retourne le montant calculé du résultat de fonctionnement de l'année
 * spécifiée en paramètre.
 * @param observations
 * @param year
 */
export function getCalculatedOperationResultAmount(
	observations: Model.Observation[],
	year        : number
) {
	return getCalculatedResultAmount(observations, year, true)
}

/**
 * Retourne le montant calculé du résultat de fonctionnement des années
 * spécifiées en paramètre.
 * @param observations
 * @param years
 */
export function getCalculatedOperationResultAmounts(
	observations: Model.Observation[],
	years       : number[]
) {
	return years.map(year => (
		getCalculatedOperationResultAmount(observations, year)
	))
}

/**
 * Retourne le montant calculé du résultat de fonctionnement des années
 * spécifiées en paramètre pour la prospective.
 * @param localAuthority
 * @param ppiYearLoans
 * @param ppi
 * @param scenario
 * @param years
 * @param currentYear
 */
export function getProspectiveCalculatedOperationResultAmounts(
	localAuthority: Model.LocalAuthority,
	ppiYearLoans: Model.PpiYearLoan[],
	ppi: Model.Ppi,
	scenario: Model.Scenario,
	years: number[],
	currentYear: number
): number[] {
	return years.map(year => {
		const ppiYear = PpiDomain.findPpiYearByYear(ppi.years, year)
		if (!ppiYear) return 0
		return reduceTotalPpi(
			ppiYear,
			ppi,
			ppiYearLoans,
			[],
			currentYear,
			scenario,
			localAuthority
		)
	})
}

/**
 * Retourne le montant calculé du résultat de fonctionnement des années
 * spécifiées en paramètre pour la prospective.
 * @param localAuthority
 * @param ppiYearLoans
 * @param ppi
 * @param scenario
 * @param years
 * @param currentYear
 */
export function getProspectiveCalculatedOperationResultAmountForAYear(
	localAuthority: Model.LocalAuthority,
	ppiYearLoans: Model.PpiYearLoan[],
	ppi: Model.Ppi,
	scenario: Model.Scenario,
	year: number
): number {
	const ppiYear = PpiDomain.findPpiYearByYear(ppi.years, year)
	if (!ppiYear) return 0
	return reduceTotalPpi(
		ppiYear,
		ppi,
		ppiYearLoans,
		[],
		year,
		scenario,
		localAuthority
	)
}

/**
 * Retourne le montant calculé de la capacité d'autofinancement brute des années
 * spécifiées en paramètre pour la prospective.
 * @param localAuthority
 * @param operations
 * @param ppi
 * @param scenario
 * @param decomposedLoans
 * @param years
 */
export function getProspectiveCalculatedGrossCashFlow(
	localAuthority: Model.LocalAuthority,
	operations: Operations,
	ppi: Model.Ppi,
	scenario: Model.Scenario,
	decomposedLoans: Model.LoanWithDecomposition[],
	years: number[],
	priorities: number[] = []
): number[] {
	const loans = PpiDomain.getPpiYearLoans(decomposedLoans, ppi)
	return years.map(year => {
		const ppiYear = PpiDomain.findPpiYearByYear(ppi.years, year)
		if (!ppiYear) return 0
		if (!scenario) return ppiYear.selfFinancing
		return _aggregateCAFBrute(
			"prospective",
			year,
			localAuthority,
			operations,
			scenario,
			priorities,
			loans
		)
	})
}

/**
 * Retourne le montant calculé du résultat d'investissement de l'année
 * spécifiée en paramètre.
 * @param observations
 * @param year
 */
export function getCalculatedInvestmentResultAmount(
	observations: Model.Observation[],
	year        : number
): number {
	return getCalculatedResultAmount(observations, year, false)
}

/**
 * Retourne le montant calculé du résultat d'investissement des années
 * spécifiées en paramètre.
 * @param observations
 * @param years
 */
export function getCalculatedInvestmentResultAmounts(
	observations: Model.Observation[],
	years       : number[]
): number[] {
	return years.map(year => (
		getCalculatedInvestmentResultAmount(observations, year)
	))
}

/**
 * Retourne le montant calculé du résultat d'investissement des années
 * spécifiées en paramètre pour la prospective.
 * @param localAuthority
 * @param operations
 * @param years
 */
export function getProspectiveCalculatedInvestmentResultAmounts(
	localAuthority: Model.LocalAuthority,
	operations    : Operations,
	years         : number[]
): number[] {
	return years.map(year => (
		_aggregateOrderOperationsTotal("prospective", year, localAuthority, operations, false)
	))
}

/**
 * Retourne le montant calculé du résultat de l'année spécifiée en paramètre,
 * soit en section fonctionnement (operation == true), soit en section
 * investissement (operation == false), à partir de la liste d'observations
 * fournie en paramètre.
 * @param observations
 * @param year
 * @param operation
 */
function getCalculatedResultAmount(
	observations: Model.Observation[],
	year        : number,
	operation   : boolean
): number {
	let amount = 0

	let receiptsObservations: Model.Observation[]
	let expenseObservations : Model.Observation[]

	const observationReceipts = observations.filter(o => o.account.receipts);
	const observationExpense = observations.filter(o => o.account.expense);

	if (operation) {
		receiptsObservations = ObservationDomain.getOperationReceiptsObservations(observationReceipts, year)
		expenseObservations  = ObservationDomain.getOperationExpenseObservations(observationExpense, year)
	} else {
		receiptsObservations = ObservationDomain.getInvestmentReceiptsObservations(observationReceipts, year)
		expenseObservations  = ObservationDomain.getInvestmentExpenseObservations(observationExpense, year)
	}

	receiptsObservations.forEach(observation => { amount += observation.amount })
	expenseObservations.forEach(observation => { amount -= observation.amount })

	return amount
}

/**
 * Retourne le résultat d'investissement net de l'année spécifiée en
 * paramètre, à partir des listes d'opérations et des résultats spécifiées en
 * paramètres.
 * Le résultat cumulé d'investissement est la somme du résulat
 * d'investissement calculé de l'année N, du résultat enregistré de l'année
 * N-1 et du total du reste à réaliser de l'année N.
 * @param observations
 * @param results
 * @param rests
 * @param year
 */
export function getCumulativeInvestmentResultAmount(
	observations: Model.Observation[],
	results     : Model.Result[],
	rests       : Model.Rest[],
	year        : number
): number {
	const resultAmount        = getCalculatedInvestmentResultAmount(observations, year)
	const savedPrevYearResult = getSavedInvestmentResult(results, year - 1)
	const totalRestAmount     = RestDomain.getTotalRestAmount(rests, year)

	return resultAmount
	+ (savedPrevYearResult ? savedPrevYearResult.amount : 0)
	+ totalRestAmount
}

/**
 * Retourne le résultat d'investissement net des années spécifiées en
 * paramètres, à partir des listes d'opérations et de résultats spécifiées en
 * paramètre.
 * @param observations
 * @param results
 * @param rests
 * @param years
 */
export function getCumulativeInvestmentResultAmounts(
	observations: Model.Observation[],
	results     : Model.Result[],
	rests       : Model.Rest[],
	years       : number[]
): number[] {
	return years.map(year => (
		getCumulativeInvestmentResultAmount(observations, results, rests, year)
	))
}

/**
 * Retourne le résultat de fonctionnement net de l'année spécifiée en
 * paramètre, à partir des listes d'opérations et des résultats spécifiées en
 * paramètres.
 * Le résultat net est la somme du résultat de fonctionnement calculé de
 * l'année N et du résultat enregistré de l'année N-1.
 * @param observations
 * @param results
 * @param year
 */
function getNetOperationResultAmount(
	observations: Model.Observation[],
	results     : Model.Result[],
	year        : number
): number {
	const resultAmount        = getCalculatedOperationResultAmount(observations, year)
	const savedPrevYearResult = getSavedOperationResult(results, year - 1)

	return resultAmount + (savedPrevYearResult ? savedPrevYearResult.amount : 0)
}

/**
 * Retourne le résultat de fonctionnement net des années spécifiées en
 * paramètres, à partir des listes d'opérations et de résultats spécifiées en
 * paramètre.
 * @param observations
 * @param results
 * @param years
 */
export function getNetOperationResultAmounts(
	observations: Model.Observation[],
	results     : Model.Result[],
	years       : number[]
): number[] {
	return years.map(year => (
		getNetOperationResultAmount(observations, results, year)
	))
}

/**
 * Retourne le résultat de fonctionnement de l'année spécifiée en paramètre ou
 * null.
 * @param results
 * @param year
 */
export function getSavedOperationResult(
	results: Model.Result[],
	year   : number
): Model.Result {
	return getSavedResult(results, year, true)
}

/**
 * Retourne parmi la liste spécifiée en paramètre, la liste des résultats de
 * fonctionnement des années spécifiées en paramètres.
 * @param results
 * @param years
 */
export function getSavedOperationResults(
	results: Model.Result[],
	years  : number[]
): Model.Result[] {
	return years.map(year => (
		getSavedOperationResult(results, year)
	))
}

/**
 * Retourne le résultat d'investissement de l'année spécifiée en paramètre ou
 * null.
 * @param results
 * @param year
 * @param operation
 */
export function getSavedInvestmentResult(
	results: Model.Result[],
	year   : number
): Model.Result {
	return getSavedResult(results, year, false)
}

/**
 * Retourne parmi la liste spécifiée en paramètre, les résultats
 * d'investissement des années spécifiées en paramètres.
 * @param results
 * @param years
 */
export function getSavedInvestmentResults(
	results: Model.Result[],
	years  : number[]
): Model.Result[] {
	return years.map(year => (
		getSavedInvestmentResult(results, year)
	))
}

/**
 * Retourne le résultat de l'année spécifiée en paramètre parmi la liste de
 * résultats spécifiée en paramètre ou null.
 * @param results
 * @param year
 * @param operation
 */
function getSavedResult(
	results  : Model.Result[],
	year     : number,
	operation: boolean
): Model.Result {
	return results.find(result => (
		year === result.year && operation === result.operation
	)) || null
}



// WEBPACK FOOTER //
// ./src/domain/sfp/result-domain.ts